import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MaskedLine, Reveal, SectionHeading } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/components/CtaBand";
import { ProjectCard } from "@/components/ProjectCard";
import { waLink } from "@/lib/whatsapp";
import { images } from "@/content/images";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

const Hero = () => {
  const { t } = useLang();
  const h = t.home.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pb-16 pt-32 sm:pt-36"
    >
      <div className="wrap grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.p {...fade(0.1)} className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-teak" />
            {h.eyebrow}
          </motion.p>
          <h1 className="mt-6 font-display text-[clamp(2.9rem,7vw,6.6rem)] font-light leading-[0.98] tracking-tight text-espresso">
            <MaskedLine delay={0.25}>{h.line1}</MaskedLine>
            <MaskedLine delay={0.4} className="italic text-teak">
              {h.line2}
            </MaskedLine>
          </h1>
          <motion.p
            {...fade(0.7)}
            className="mt-7 max-w-xl text-sm font-light leading-relaxed text-charcoal/70 sm:text-base"
          >
            {h.sub}
          </motion.p>
          <motion.div {...fade(0.85)} className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={waLink(t.nav.waMessage)}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-cta-button"
              className="btn-dark"
            >
              <MessageCircle className="h-4 w-4" />
              {h.ctaSecondary}
            </a>
            <Link
              to="/portfolio"
              data-testid="hero-explore-portfolio-button"
              className="btn-outline group"
            >
              {h.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
          <motion.p
            {...fade(1)}
            className="mt-12 font-grotesk text-[11px] uppercase tracking-[0.25em] text-taupe"
          >
            {h.location}
          </motion.p>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] w-full overflow-hidden rounded-sm"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={images.hero}
              alt={h.heroCaption}
              style={{ y: imgY }}
              className="absolute inset-0 h-[115%] w-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -bottom-8 -left-6 hidden w-44 overflow-hidden rounded-sm border-4 border-offwhite shadow-xl md:block lg:-left-14 lg:w-52"
          >
            <img
              src={images.heroDetail}
              alt=""
              loading="lazy"
              className="aspect-square w-full object-cover"
            />
          </motion.div>
          <p className="mt-4 text-right font-grotesk text-[11px] uppercase tracking-[0.2em] text-taupe md:pl-28">
            {h.heroCaption}
          </p>
        </div>
      </div>

      <motion.p
        {...fade(1.2)}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-3 font-grotesk text-[10px] uppercase tracking-[0.3em] text-taupe sm:flex"
      >
        {h.scroll}
        <span className="h-8 w-px bg-taupe/40" />
      </motion.p>
    </section>
  );
};

const ServicesBento = () => {
  const { t } = useLang();
  const cards = t.services.slice(0, 6);
  const spans = [
    "md:col-span-8",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-12",
  ];
  const featured = (i) => i === 0 || i === 5;

  return (
    <section className="py-20 sm:py-28 lg:py-32" data-testid="home-services">
      <div className="wrap">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            eyebrow={t.home.services.eyebrow}
            title={t.home.services.title}
            sub={t.home.services.sub}
          />
          <Reveal delay={0.1}>
            <Link
              to="/services"
              data-testid="home-services-view-all"
              className="group mb-1 inline-flex items-center gap-2 text-sm text-espresso transition-colors hover:text-teak"
            >
              {t.home.services.link}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
          {cards.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08} className={spans[i]}>
              <Link
                to="/services"
                data-testid={`bento-service-card-${s.id}`}
                className={`group block h-full w-full overflow-hidden rounded-sm ${
                  featured(i) ? "relative min-h-[340px] lg:min-h-[440px]" : "border border-sand bg-beige/50"
                }`}
              >
                {featured(i) ? (
                  <>
                    <img
                      src={s.img}
                      alt={s.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-espresso/75 via-espresso/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
                      <p className="font-grotesk text-[11px] uppercase tracking-[0.25em] text-terracotta">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                      <h3 className="mt-2 font-display text-3xl font-light text-offwhite sm:text-4xl">
                        {s.name}
                      </h3>
                      <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-offwhite/75">
                        {s.tagline}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex h-full flex-col">
                    <div className="overflow-hidden">
                      <img
                        src={s.img}
                        alt={s.name}
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="font-display text-2xl font-light text-espresso">{s.name}</h3>
                        <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-teak transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      </div>
                      <p className="mt-2 text-sm font-light leading-relaxed text-charcoal/65">
                        {s.tagline}
                      </p>
                    </div>
                  </div>
                )}
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Philosophy = () => {
  const { t } = useLang();
  const p = t.home.philosophy;
  return (
    <section className="border-y border-sand/70 bg-beige/50 py-20 sm:py-28 lg:py-32" data-testid="home-philosophy">
      <div className="wrap grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <Reveal>
              <p className="eyebrow">{p.eyebrow}</p>
            </Reveal>
          </div>
          <MaskedLine mode="view" className="mt-5">
            <h2 className="font-display text-3xl font-light leading-[1.15] tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              {p.title}
            </h2>
          </MaskedLine>
        </div>
        <div className="lg:col-span-5 lg:pt-10">
          <Reveal>
            <p className="text-sm font-light leading-relaxed text-charcoal/75 sm:text-base">{p.p1}</p>
            <p className="mt-5 text-sm font-light leading-relaxed text-charcoal/75 sm:text-base">
              {p.p2}
            </p>
            <Link
              to="/about"
              data-testid="home-philosophy-about-link"
              className="group mt-8 inline-flex items-center gap-2 border-b border-teak pb-1 text-sm font-medium text-espresso transition-colors hover:text-teak"
            >
              {p.link}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="group mt-12 overflow-hidden rounded-sm" data-testid="home-philosophy-image">
              <img
                src={images.texture}
                alt={p.caption}
                loading="lazy"
                className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <p className="mt-3 font-grotesk text-[11px] uppercase tracking-[0.2em] text-taupe">
                {p.caption}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const PortfolioPreview = () => {
  const { t } = useLang();
  const picks = [t.projects[0], t.projects[1], t.projects[4], t.projects[3], t.projects[6]];
  const cells = [
    "md:col-span-8 h-[300px] lg:h-[440px]",
    "md:col-span-4 h-[300px] lg:h-[440px]",
    "md:col-span-4 h-[280px] lg:h-[360px]",
    "md:col-span-4 h-[280px] lg:h-[360px]",
    "md:col-span-4 h-[280px] lg:h-[360px]",
  ];
  return (
    <section className="py-20 sm:py-28 lg:py-32" data-testid="home-portfolio">
      <div className="wrap">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            eyebrow={t.home.portfolio.eyebrow}
            title={t.home.portfolio.title}
          />
          <Reveal delay={0.1}>
            <Link
              to="/portfolio"
              data-testid="home-portfolio-view-all"
              className="group mb-1 inline-flex items-center gap-2 text-sm text-espresso transition-colors hover:text-teak"
            >
              {t.home.portfolio.link}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
          {picks.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08} className={cells[i]}>
              <Link to="/portfolio" data-testid={`home-portfolio-card-${p.id}`} className="block h-full w-full">
                <ProjectCard p={p} testid={`home-project-card-${p.id}`} />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Process = () => {
  const { t } = useLang();
  const p = t.home.process;
  return (
    <section className="border-t border-sand/70 py-20 sm:py-28" data-testid="home-process">
      <div className="wrap">
        <SectionHeading eyebrow={p.eyebrow} title={p.title} />
        <div className="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {p.steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="group border-t border-espresso/15 pt-6 transition-colors duration-300 hover:border-teak">
                <p className="font-grotesk text-sm tracking-[0.2em] text-teak">{s.n}</p>
                <h3 className="mt-4 font-display text-xl font-light text-espresso sm:text-2xl">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-charcoal/65">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  return (
    <>
      <Hero />
      <Marquee items={t.home.marquee} />
      <Philosophy />
      <ServicesBento />
      <PortfolioPreview />
      <Process />
      <CtaBand />
    </>
  );
}
